import React, { useEffect, useState } from 'react';

import { Entry } from 'contentful';
import { NextPageWithLayout } from 'next';
import NextImage from 'next/image';

import { BlogPreviewCard } from '../../../components/Blog/BlogPreviewCard/BlogPreviewCard';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { Container } from '../../../components/Container/Container';
import { DownloadAppVerticalCTA } from '../../../components/CTAs/DownloadAppVerticalCTA/DownloadAppVerticalCTA';
import { Grid } from '../../../components/Grid/Grid';
import { Heading } from '../../../components/Heading/Heading';
import { MetaTags } from '../../../components/MetaTags/MetaTags';
import { Paginator } from '../../../components/Paginator/Paginator';
import { Text } from '../../../components/Text/Text';
import { getRandomInteger } from '../../../helpers/number';
import { TypeBlogPostSkeleton } from '../../../interfaces/contentful';
import blogHeaderReel from '../assets/blogHeaderReel.png';
import { PostsType } from '../helpers/constants';
import { getBlogBreadcrumbs } from '../helpers/getBlogBreadcrumbs';

import { getBlogMetaData } from './helpers/getBlogMetaData';
import { getBlogTitle } from './helpers/getBlogTitle';

export interface IBlogPostsProps {
  posts: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[];
  page: number;
  endPage: number;
  title?: string;
  canonicalPath: string;
  type: PostsType;
  paginationPath?: string;
}

export const BlogPosts: NextPageWithLayout<IBlogPostsProps> = ({
  posts,
  page,
  endPage,
  title,
  canonicalPath,
  type,
  paginationPath,
}) => {
  const pageTitle = getBlogTitle(type, title);
  const titleAndPageNumber = `${pageTitle}${page > 1 ? ` - page ${page}` : ``}`;
  const [randomAddCopyVarient, setRandomAddCopyVarient] = useState(0);
  const blogMetadata = getBlogMetaData(type, page, title);
  const { metadataTitle, metadataDescription } = blogMetadata;

  useEffect(() => {
    setRandomAddCopyVarient(getRandomInteger(1, 2));
  }, []);

  return (
    <div className="mb-12">
      <MetaTags
        link={canonicalPath}
        title={metadataTitle}
        description={metadataDescription}
        image={blogHeaderReel.src}
      />
      <Container className="-px-6 sm:-px-11 md:-px-14">
        <div className="relative w-full bg-cover py-8 text-start sm:py-10 md:py-12">
          <NextImage
            alt="background"
            className="size-full object-cover"
            src={blogHeaderReel}
            fill
            priority
            placeholder="blur"
            sizes="100vw"
          />
          <div className="relative px-6 sm:w-2/3 sm:px-11 md:px-14">
            <Heading as="h1" className="m-0">
              {titleAndPageNumber}
            </Heading>
            {type === 'all' && (
              <Text className="m-0 w-1/2 md:w-2/3">
                Your guide to fishing, the world’s most popular hobby.
              </Text>
            )}
          </div>
        </div>
      </Container>

      {title && (
        <Container>
          <div className="mt-4 text-left">
            <Breadcrumbs items={getBlogBreadcrumbs(type, title)} />
          </div>
        </Container>
      )}

      <Container>
        <Grid>
          <div
            className={`col-start-1 col-end-5 row-start-1 grid h-max grid-cols-1 gap-8 pt-8 sm:col-end-13 ${page === 1 ? 'md:col-end-9' : ''}`}
          >
            {posts.map(blogPost => {
              const {
                slug,
                title: blogPostTitle,
                postDescription,
                subjectTags,
                species,
                headerImage,
                category,
                publishDate,
              } = blogPost.fields;
              const _id = blogPost.sys.id;
              const imageUrl = headerImage?.fields.file?.url;
              const categorySlug = category?.fields.slug;

              return categorySlug ? (
                <BlogPreviewCard
                  key={_id}
                  imageUrl={imageUrl}
                  categorySlug={categorySlug}
                  blogPostSlug={slug}
                  blogPostTitle={blogPostTitle}
                  blogPostDescription={postDescription}
                  blogTags={subjectTags}
                  species={species}
                  publishDate={publishDate}
                />
              ) : (
                <></>
              );
            })}
          </div>
          {page === 1 && (
            <div className="col-start-1 col-end-5 row-start-2 sm:col-end-13 md:col-start-9 md:col-end-13 md:row-start-1 md:ml-6 md:block">
              <div className="top-24 mt-8 md:sticky">
                <DownloadAppVerticalCTA version={randomAddCopyVarient} />
              </div>
            </div>
          )}
        </Grid>
      </Container>
      {endPage !== 0 && endPage !== 1 && (
        <div className="mt-6">
          <Paginator
            startPage={1}
            endPage={endPage}
            active={page}
            paginationPath={paginationPath}
          />
        </div>
      )}
    </div>
  );
};
